import { BillingModel } from '@/Enums/BillingModel.enum';
import { isRoleAllowedCpm, isRoleAllowedRateCardDimension, isSuperAdminRole } from '@/helpers/roles';
import { RootState } from '@/store';
import { createSelector } from '@reduxjs/toolkit';
import { selectWhitelabelBillingModel, selectWhitelabelTableSettings } from '../currentWhitelabel.selectors';
import { selectMeRole } from '../me.selectors';
import { parsedWithBeeswaxClientsSelector } from '../sharedApi.selectors';

const selectBeeswaxReportBuilder = (state: RootState) => state.beeswaxReportBuilder;

export const selectBeeswaxTemplates = createSelector(
  [selectBeeswaxReportBuilder],
  ({ templates, templatesRequestId }) => ({
    templates,
    loadingTemplates: !!templatesRequestId,
  }),
);

export const selectBeeswaxCampaigns = createSelector(
  [selectBeeswaxReportBuilder],
  ({ campaigns, campaignsRequestId }) => ({
    campaigns,
    loadingCampaigns: !!campaignsRequestId,
  }),
);

export const selectBeeswaxEvents = createSelector([selectBeeswaxReportBuilder], ({ events, eventsRequestId }) => ({
  events,
  loadingEvents: !!eventsRequestId,
}));

export const selectBeeswaxGeneratedReports = createSelector(
  [selectBeeswaxReportBuilder],
  ({ generatedReports, generatedReportsRequestId, generatedReportsTotal }) => ({
    generatedReports,
    loadingGeneratedReports: !!generatedReportsRequestId,
    generatedReportsTotal,
  }),
);

export const selectBeeswaxScheduledReports = createSelector(
  [selectBeeswaxReportBuilder],
  ({ scheduledReports, scheduledReportsRequestId, scheduledReportsTotal }) => ({
    scheduledReports,
    loadingScheduledReports: !!scheduledReportsRequestId,
    scheduledReportsTotal,
  }),
);

export const selectReportBuilderBeeswaxClients = createSelector([parsedWithBeeswaxClientsSelector], clients => clients);

export const selectShowRateCard = createSelector(
  [selectWhitelabelBillingModel, selectMeRole],
  (billingModel, meRole) => {
    if (isSuperAdminRole(meRole)) {
      return true;
    }
    return isRoleAllowedRateCardDimension(meRole) && billingModel === BillingModel.RATE_CARD;
  },
);

// Mirrors the Reporting Dashboard gate: CPM-allowed role plus the org's Client Spend toggle.
export const selectShowClientSpend = createSelector(
  [selectWhitelabelTableSettings, selectMeRole],
  ({ showClientSpend }, meRole) => isRoleAllowedCpm(meRole) && !!showClientSpend,
);

export const selectShowTotalSpend = createSelector(
  [selectWhitelabelTableSettings, selectMeRole],
  ({ showCpm }, meRole) => isRoleAllowedCpm(meRole) && !!showCpm,
);

export const selectShowLineItemAltId = createSelector(
  [selectWhitelabelTableSettings],
  ({ showLineItemAlternativeId }) => !!showLineItemAlternativeId,
);

export const selectShowCampaignAltId = createSelector(
  [selectWhitelabelTableSettings],
  ({ showCampaignAlternativeId }) => !!showCampaignAlternativeId,
);

export const selectShowAdvertiserAltId = createSelector(
  [selectWhitelabelTableSettings],
  ({ showAdvertiserAlternativeId }) => !!showAdvertiserAlternativeId,
);
